import type { ReactNode } from 'react';
import { useQuery } from '@apollo/client/react';
import { Camera, ImageOff, Loader2, MonitorX, WifiOff } from 'lucide-react';
import { STAFF_PROXY_FETCH_DEVICE_SCREENSHOT } from '@staff-app/graphql-client';
import { Button, cn } from '@staff-app/ui';
import { safeDistanceToNow } from '@/lib/safe-format';
import { VERDICT_DISPLAY, type ScreenHealthVerdict } from './lib/screen-health';
import type { TakeScreenEntry } from './lib/types';

interface ScreenshotResult {
  staffProxyFetchDeviceScreenshot: { imageBase64: string; mimeType: string } | null;
}

/** scio3 DeviceContentTab `Section` — a titled card of label/value rows; empty values are dropped. */
function Section({ title, testId, rows }: { title: string; testId: string; rows: Array<[string, ReactNode]> }) {
  const visible = rows.filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (visible.length === 0) return null;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-sm" data-testid={testId}>
      <div className="mb-2 text-xs font-semibold text-muted-foreground">{title}</div>
      <dl className="grid grid-cols-[max-content_1fr] items-baseline gap-x-4 gap-y-2">
        {visible.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-xs text-muted-foreground">{label}</dt>
            <dd className="break-all text-sm font-medium" data-testid={`${testId}-${label.toLowerCase()}`}>
              {value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * Content tab — scio3's `OnTheGlassCapture` + `DeviceContentTab`: the latest captured frame with the
 * Screen-Health verdict chip top-left, Recapture top-right and "captured X ago" bottom-left, then a
 * "Now playing" card (Type · Name · Since · Duration · Expired).
 *
 * `takeScreens` is newest-first, so the latest is index 0. The bytes come through
 * `staffProxyFetchDeviceScreenshot`, which signs the file-cdn URL server-side (the raw S3 key 403s in
 * CDN-configured envs) and checks the key is on THIS device's own `takeScreens`.
 *
 * Recapture runs the drawer's capture flow (the same one the action bar's Take screenshot uses):
 * staff-api captures, waits for the new frame and returns its key; the drawer then refetches detail so
 * `takeScreens[0]` — and this viewer — move to the new frame. Omitted when the device can't take one
 * (no `onCaptureNow`), disabled with a reason when it can't right now.
 */
export function ContentTab({
  deviceId,
  accountId,
  takeScreens,
  verdict,
  hdmiActive,
  appOnline,
  currentType,
  nowPlayingName,
  selectionDate,
  documentDuration,
  contentExpired,
  onCaptureNow,
  capturing = false,
  captureBlockedReason,
  captureNote,
}: {
  deviceId: string;
  accountId: string | null | undefined;
  takeScreens: TakeScreenEntry[] | null | undefined;
  verdict: ScreenHealthVerdict;
  hdmiActive: boolean | null | undefined;
  appOnline: boolean | undefined;
  currentType: string | null | undefined;
  nowPlayingName: string | null | undefined;
  selectionDate: string | null | undefined;
  documentDuration: number | null | undefined;
  contentExpired: boolean | null | undefined;
  onCaptureNow?: () => void;
  capturing?: boolean;
  captureBlockedReason?: string | null;
  /** Why the last capture didn't produce a frame (blocked / timeout / …), shown under the viewer. */
  captureNote?: string | null;
}) {
  const latest = takeScreens?.length ? takeScreens[0] : null;
  const imageKey = latest?.image ?? '';

  const { data, loading, error } = useQuery<ScreenshotResult>(STAFF_PROXY_FETCH_DEVICE_SCREENSHOT, {
    variables: { accountId: accountId ?? '', deviceId, imageKey },
    skip: !imageKey || !accountId,
    fetchPolicy: 'cache-first',
  });

  const img = data?.staffProxyFetchDeviceScreenshot;
  const src = img?.imageBase64 ? `data:${img.mimeType || 'image/png'};base64,${img.imageBase64}` : null;
  const shotAge = latest?.createdAt ? safeDistanceToNow(latest.createdAt, {}, '') : '';
  const offline = appOnline === false;
  const screenOff = hdmiActive === false;

  const v = VERDICT_DISPLAY[verdict];
  const VerdictIcon = v.Icon;

  const since = selectionDate ? safeDistanceToNow(selectionDate, { addSuffix: true }, '') : '';
  const duration =
    typeof documentDuration === 'number' && documentDuration > 0 ? `${documentDuration}s` : null;

  return (
    <div className="space-y-4" data-testid="testing-content-tab">
      <div className="overflow-hidden rounded-lg border bg-card shadow-sm" data-testid="testing-screenshot">
        <div className="relative aspect-video w-full bg-muted">
          {src ? (
            <img
              src={src}
              alt={'Latest screenshot'}
              className={cn('h-full w-full object-cover', offline && 'opacity-50 grayscale')}
              data-testid="testing-screenshot-image"
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 text-muted-foreground">
              {loading ? (
                <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
              ) : (
                <>
                  <ImageOff className="h-7 w-7 opacity-50" aria-hidden />
                  <span className="text-xs font-medium" data-testid="testing-screenshot-empty">
                    {latest
                      ? error
                        ? "Couldn't load the latest screenshot"
                        : 'Screenshot unavailable'
                      : 'No recent screenshot'}
                  </span>
                  {!latest && onCaptureNow && !captureBlockedReason ? (
                    <span className="text-[11px] text-muted-foreground/70">
                      {'Capture one to see the screen'}
                    </span>
                  ) : null}
                </>
              )}
            </div>
          )}

          {/* Verdict chip — top-left. */}
          <span
            className={cn(
              'absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-background/85 px-2 py-0.5 text-[11px] font-medium shadow-sm backdrop-blur',
              v.toneClass,
            )}
            data-testid="testing-screenshot-verdict"
          >
            <VerdictIcon className="h-3.5 w-3.5" aria-hidden />
            {v.label}
          </span>

          {/* Capture / Recapture — top-right. */}
          {onCaptureNow ? (
            <Button
              variant="secondary"
              size="xs"
              onClick={onCaptureNow}
              disabled={capturing || !!captureBlockedReason}
              disabledTooltip={captureBlockedReason || undefined}
              aria-label={src ? 'Recapture' : 'Capture now'}
              className="absolute right-2 top-2 gap-1 bg-background/85 shadow-sm backdrop-blur"
              data-testid="testing-capture-now"
            >
              {capturing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Camera className="h-3.5 w-3.5" aria-hidden />
              )}
              {src ? 'Recapture' : 'Capture now'}
            </Button>
          ) : null}

          {/* Freshness — bottom-left, only when a shot exists. */}
          {src && shotAge ? (
            <span
              className="absolute bottom-2 left-2 rounded bg-background/75 px-1.5 py-0.5 text-[10px] text-muted-foreground backdrop-blur"
              data-testid="testing-screenshot-age"
            >
              {`captured ${shotAge} ago`}
            </span>
          ) : null}

          {capturing ? (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-background/60 text-xs font-medium text-muted-foreground"
              data-testid="testing-screenshot-capturing"
            >
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
              {'Capturing — waiting for the device to upload a new frame…'}
            </div>
          ) : null}
        </div>

        {/* Guard strip — why capture is unavailable. */}
        {screenOff ? (
          <div className="flex items-center gap-1.5 border-t px-3 py-2 text-xs text-amber-700 dark:text-amber-500">
            <MonitorX className="h-3.5 w-3.5" aria-hidden />
            {'Screen is off — nothing on the glass to capture.'}
          </div>
        ) : offline ? (
          <div className="flex items-center gap-1.5 border-t px-3 py-2 text-xs text-muted-foreground">
            <WifiOff className="h-3.5 w-3.5" aria-hidden />
            {src
              ? 'Device is offline — showing the last captured frame.'
              : 'Device is offline — no capture available.'}
          </div>
        ) : null}
        {captureNote ? (
          <div
            className="border-t bg-warning/10 px-3 py-2 text-xs text-warning"
            data-testid="testing-capture-note"
          >
            {captureNote}
          </div>
        ) : null}
      </div>

      <Section
        title={'Now playing'}
        testId="testing-now-playing-card"
        rows={[
          ['Type', currentType ?? null],
          ['Name', nowPlayingName ?? null],
          ['Since', since || null],
          ['Duration', duration],
          ['Expired', contentExpired ? 'Yes' : 'No'],
        ]}
      />
    </div>
  );
}
